import type { CSSProperties } from 'react';
import { IconBack } from './components/icons';

// Trust/Legal P1 — the two static legal pages (Privacy + Terms), publicly readable (no auth required — these must
// be reachable by an unauthenticated visitor, e.g. from the Landing footer or a direct link/bookmark). Fixed
// content objects only; no CMS, no per-user data. LEGAL TEXTS REQUIRE OWNER APPROVAL BEFORE DEPLOY (see CHANGELOG).

const LAST_UPDATED = '2026-08-03';

interface Section { h: string; p?: string; items?: string[]; linkHref?: string; linkLabel?: string }
interface Doc { title: string; sections: Section[] }

// [owner: confirm legal name] — "Zonive Innovations" is the drafted operating name; owner to confirm the exact
// legal entity name before this copy ships.
const PRIVACY: Doc = {
  title: 'Privacy Policy',
  sections: [
    { h: 'Who we are', p: 'CVCraft is operated by Zonive Innovations, based in Canada.' },
    { h: 'What we collect', p: 'Your account email; the CV content you type or import; payment is handled by LemonSqueezy as Merchant of Record — card details never reach us.' },
    { h: 'How your CV text is processed', p: "Your CV text is sent to Anthropic's API (US) solely to generate the assessments you request. It is not used by us to train models. Our assessment tool is diagnostic and best-effort — it asks questions rather than writing content for you, and we enforce that server-side, though no automated safeguard is absolute." },
    { h: 'Cookies', p: 'A session cookie keeps you signed in. No ad trackers.' },
    { h: 'Data location', p: 'Cloudflare infrastructure; processing as described above.' },
    { h: 'Retention & deletion', p: 'Your data stays while your account is active. Deleting your account deactivates it immediately with a 7-day sign-in recovery window, after which it is queued for permanent removal from active systems. Backups age out on a rolling schedule.' },
    { h: 'Your rights', p: 'Access, correction, or deletion — email support@cvcraft.ca.' },
    { h: 'No sale of personal information', p: 'We do not sell your personal information, and we do not use third-party advertising.' },
    { h: 'Contact', p: 'support@cvcraft.ca' },
  ],
};

const TERMS: Doc = {
  title: 'Terms of Service',
  sections: [
    { h: 'The service', p: 'CVCraft provides per-specialty CV structuring and assessment for CaRMS applicants. Assessments are informational, based on published conventions and general patterns — they are NOT a prediction of match outcomes, program decisions, or eligibility, and NOT career, legal, or immigration advice.' },
    { h: 'Your content', p: 'You own your CV content. You grant us only the processing needed to run the service. You are responsible for the accuracy and honesty of what you submit.' },
    { h: 'The writing rule', p: 'The tool asks questions and diagnoses — it does not write your CV. Enforcement is best-effort.' },
    { h: 'Accounts', p: 'Sign-in uses a magic link sent to your email. Keep your email secure.' },
    { h: 'Payments & refunds', p: 'Purchases are processed by LemonSqueezy as Merchant of Record. Prices shown at checkout are in CAD. For refund requests, contact support@cvcraft.ca and we will review promptly — refunds are processed through LemonSqueezy.', linkHref: '/refunds', linkLabel: 'Read our full Refund Policy' },
    { h: 'Acceptable use', p: "No scraping, abuse, or reverse engineering, and no submitting others' personal data without authority." },
    { h: 'Availability', p: 'The service is provided as-is; we may change or discontinue features.' },
    { h: 'Liability', p: 'To the extent permitted by law, our total liability is limited to the amount you paid in the 12 months before the claim.' },
    { h: 'Governing law', p: 'Alberta, Canada.' },
    { h: 'Changes', p: "We'll post updates here with the date." },
    { h: 'Contact', p: 'support@cvcraft.ca' },
  ],
};

// Polish B2 — the Refund Policy (/refunds, linked from Terms' Payments section + the footer). Text kept
// EXACTLY consistent with the shipped Terms wording (contact support@cvcraft.ca, reviewed promptly, processed
// through LemonSqueezy). OWNER APPROVES THE TEXT at deploy-hold, same as Privacy/Terms.
const REFUNDS: Doc = {
  title: 'Refund Policy',
  sections: [
    { h: 'How refunds work', p: 'For refund requests, contact support@cvcraft.ca and we will review promptly — refunds are processed through LemonSqueezy, our Merchant of Record for all payments.' },
    { h: 'What a refund covers', p: 'Refunds apply to the per-CV unlock and to unused bundle credits (the 3-CV bundle credits you have not yet redeemed).' },
    { h: 'What is generally not refundable', p: 'Already-used credits and completed evaluations are generally not refundable, since the service — the unlock, the read, the export — has already been delivered.' },
    { h: 'When to contact us', p: 'For the fastest handling, contact us within 14 days of your purchase.' },
    { h: 'Your rights', p: "This policy does not override any consumer-law rights you have under applicable law." },
    { h: 'Contact', p: 'support@cvcraft.ca' },
  ],
};

export function LegalPage({ doc, onBack }: { doc: 'privacy' | 'terms' | 'refunds'; onBack: () => void }) {
  const d = doc === 'privacy' ? PRIVACY : doc === 'refunds' ? REFUNDS : TERMS;
  const testId = doc === 'privacy' ? 'legal-privacy' : doc === 'refunds' ? 'legal-refunds' : 'legal-terms';
  return (
    <main style={S.main} data-testid={testId}>
      <button className="btn btn-ghost" style={S.back} onClick={onBack} data-testid="legal-back"><IconBack aria-hidden="true" /> Back</button>
      <h1 style={S.h1}>{d.title}</h1>
      <p style={S.updated}>Last updated: {LAST_UPDATED}</p>
      {d.sections.map((s) => (
        <section key={s.h} style={S.section}>
          <h2 style={S.h2}>{s.h}</h2>
          {s.p && <p style={S.p}>{s.p}</p>}
          {s.items && <ul style={S.ul}>{s.items.map((it) => <li key={it}>{it}</li>)}</ul>}
          {s.linkHref && <p style={S.p}><a href={s.linkHref} data-testid={`legal-link-${s.linkHref.replace(/\W+/g, '-')}`}>{s.linkLabel}</a></p>}
        </section>
      ))}
    </main>
  );
}

const S: Record<string, CSSProperties> = {
  main: { maxWidth: 680, margin: '40px auto', padding: '0 var(--space-4) 60px', fontFamily: 'var(--sans)', lineHeight: 'var(--lh-prose)', color: 'var(--ink)' },
  back: { marginLeft: 0, paddingLeft: 0, fontSize: 'var(--fs-small)', marginBottom: 'var(--space-2)' },
  h1: { fontFamily: 'var(--serif)', fontSize: 'var(--fs-title)', margin: 'var(--space-2) 0 var(--space-1)' },
  updated: { fontSize: 'var(--fs-small)', color: 'var(--muted)', margin: '0 0 var(--space-5)' },
  section: { margin: 'var(--space-4) 0' },
  h2: { fontSize: 17, margin: '0 0 var(--space-2)' },
  p: { margin: 0, fontSize: 15, color: 'var(--ink-subtle)' },
  ul: { margin: 'var(--space-2) 0 0', paddingLeft: 20 },
};
